import { z } from 'zod';
import { digitsOnly } from '../lib/formValidation';
import type { Role } from '../lib/rbacRoles';
import { isUnifyPlatformStaffRole } from '@unify/fhir-model/staff-role-org';
import { isoDateString, nonEmptyString, optionalTrimmedString } from './common.schema';

/** Roles assignable from the Create User modal (filtered further by actor matrix in UI). */
export const createUserAssignableRoleValues = [
  'unify-admin',
  'practice-admin',
  'supervisor',
  'provider',
] as const satisfies readonly Role[];

export type CreateUserAssignableRole = (typeof createUserAssignableRoleValues)[number];

const createUserBase = {
  firstName: z.string().trim().min(1, 'First name is required'),
  lastName: z.string().trim().min(1, 'Last name is required'),
  email: z.string().trim().email('Valid email is required'),
  practiceId: z.string(),
};

export const createUserFormSchema = z
  .object({
    ...createUserBase,
    role: z.enum(createUserAssignableRoleValues),
  })
  .superRefine((data, ctx) => {
    if (!data.practiceId.trim()) {
      ctx.addIssue({
        code: 'custom',
        message: isUnifyPlatformStaffRole(data.role) ? 'Organization is required' : 'Practice is required',
        path: ['practiceId'],
      });
    }
  });

export type CreateUserFormValues = z.infer<typeof createUserFormSchema>;

/** Practice admin manual add-client (Patient invite only — no intake wizard). */
export const createClientFormSchema = z.object({
  firstName: z.string().trim().min(1, 'First name is required'),
  lastName: z.string().trim().min(1, 'Last name is required'),
  email: z.string().trim().email('Valid email is required'),
  practiceId: z.string().min(1, 'Practice is required'),
});

export type CreateClientFormValues = z.infer<typeof createClientFormSchema>;

const optionalFileSchema = z.custom<File | null>(
  (value) => value === null || value instanceof File,
  'Expected a file upload',
);

export const personalInfoSchema = z
  .object({
    businessPhone: nonEmptyString('Required'),
    firstName: nonEmptyString('Required'),
    lastName: nonEmptyString('Required'),
    preferredFirstName: z.string(),
    preferredLastName: z.string(),
    aliasFirst: z.string(),
    aliasMiddle: z.string(),
    aliasLast: z.string(),
    npi: nonEmptyString('Required'),
    nuccTaxonomyCode: z.string().nullable().refine((value) => value !== null && value.trim().length === 10, 'Required'),
    ssn: nonEmptyString('Required'),
    birthdate: isoDateString.nullable(),
    languages: z.array(z.string()).min(1, 'Required'),
    street: nonEmptyString('Required'),
    city: nonEmptyString('Required'),
    state: nonEmptyString('Required'),
    zip: nonEmptyString('Required'),
    caseload: z.string().nullable().refine((value) => value !== null && value.trim().length > 0, 'Required'),
    bio: nonEmptyString('Required'),
    headshotFile: optionalFileSchema,
    driverLicenseFile: optionalFileSchema,
  })
  .superRefine((data, ctx) => {
    if (data.businessPhone && digitsOnly(data.businessPhone).length !== 10) {
      ctx.addIssue({
        code: 'custom',
        message: 'Phone number must be exactly 10 digits',
        path: ['businessPhone'],
        input: data.businessPhone,
      });
    }

    if (data.npi && digitsOnly(data.npi).length !== 10) {
      ctx.addIssue({
        code: 'custom',
        message: 'NPI must be exactly 10 digits',
        path: ['npi'],
        input: data.npi,
      });
    }

    if (data.nuccTaxonomyCode && data.nuccTaxonomyCode.trim().length !== 10) {
      ctx.addIssue({
        code: 'custom',
        message: 'NUCC taxonomy code must be exactly 10 characters',
        path: ['nuccTaxonomyCode'],
        input: data.nuccTaxonomyCode,
      });
    }

    if (data.ssn && digitsOnly(data.ssn).length !== 9) {
      ctx.addIssue({
        code: 'custom',
        message: 'Social Security Number must be exactly 9 digits',
        path: ['ssn'],
        input: data.ssn,
      });
    }

    if (data.zip && digitsOnly(data.zip).length !== 5) {
      ctx.addIssue({
        code: 'custom',
        message: 'ZIP must be exactly 5 digits',
        path: ['zip'],
        input: data.zip,
      });
    }
  });

export type PersonalInfoFormValues = z.infer<typeof personalInfoSchema>;

export const personalInfoFieldLabels: Record<string, string> = {
  businessPhone: 'Phone number',
  firstName: 'Legal first name',
  lastName: 'Legal last name',
  npi: 'NPI number',
  nuccTaxonomyCode: 'NUCC taxonomy code',
  ssn: 'Social Security Number',
  birthdate: 'Date of birth',
  languages: 'Languages',
  street: 'Street address',
  city: 'City',
  state: 'State',
  zip: 'ZIP code',
  caseload: 'Caseload preference',
  bio: 'Bio',
  headshotFile: 'Professional headshot',
  driverLicenseFile: "Driver's license",
};

export const personalInfoSaveSchema = z
  .object({
    birthdate: isoDateString.nullable(),
    headshotFile: optionalFileSchema,
    driverLicenseFile: optionalFileSchema,
    hasExistingHeadshot: z.boolean(),
    hasExistingDriverLicense: z.boolean(),
  })
  .superRefine((data, ctx) => {
    if (!data.birthdate) {
      ctx.addIssue({
        code: 'custom',
        message: 'Required',
        path: ['birthdate'],
        input: data.birthdate,
      });
    }

    if (!data.headshotFile && !data.hasExistingHeadshot) {
      ctx.addIssue({
        code: 'custom',
        message: 'Required',
        path: ['headshotFile'],
        input: data.headshotFile,
      });
    }

    if (!data.driverLicenseFile && !data.hasExistingDriverLicense) {
      ctx.addIssue({
        code: 'custom',
        message: 'Required',
        path: ['driverLicenseFile'],
        input: data.driverLicenseFile,
      });
    }
  });

export const profileSchema = z.object({
  specialties: z.array(z.string()).min(1, 'Required'),
  modalities: z.array(z.string()).min(1, 'Required'),
  ageGroups: z.array(z.string()).min(1, 'Required'),
  gender: z.string(),
  lgbtqia: z.string(),
  religion: z.array(z.string()),
  pronouns: z.string(),
  raceEthnicity: z.array(z.string()),
});

export type ProfileFormValues = z.infer<typeof profileSchema>;

export const profileFieldLabels: Record<string, string> = {
  specialties: 'Clinical specialties',
  modalities: 'Treatment modalities',
  ageGroups: 'Preferred patient age groups',
};

/** Provider Settings → Profile tab (editable fields; mirrors onboarding personal-info + profile subsets). */
export const providerSettingsProfileTabSchema = z
  .object({
    preferredFirstName: z.string(),
    preferredLastName: z.string(),
    phone: nonEmptyString('Required'),
    street: nonEmptyString('Required'),
    city: nonEmptyString('Required'),
    state: nonEmptyString('Required'),
    zipCode: nonEmptyString('Required'),
    languages: z.array(z.string()).min(1, 'Required'),
    bio: nonEmptyString('Required'),
    pronouns: z.string(),
    genderIdentity: z.string(),
    lgbtqiaIdentity: z.string(),
    religion: z.array(z.string()),
    raceEthnicity: z.array(z.string()),
    ageGroups: z.array(z.string()).min(1, 'Required'),
    specialties: z.array(z.string()).min(1, 'Required'),
    modalities: z.array(z.string()).min(1, 'Required'),
  })
  .superRefine((data, ctx) => {
    if (digitsOnly(data.phone).length !== 10) {
      ctx.addIssue({
        code: 'custom',
        message: 'Phone number must be exactly 10 digits',
        path: ['phone'],
        input: data.phone,
      });
    }

    if (digitsOnly(data.zipCode).length !== 5) {
      ctx.addIssue({
        code: 'custom',
        message: 'ZIP must be exactly 5 digits',
        path: ['zipCode'],
        input: data.zipCode,
      });
    }
  });

export type ProviderSettingsProfileTabFormValues = z.infer<typeof providerSettingsProfileTabSchema>;

/** @deprecated Use providerSettingsProfileTabSchema */
export const providerSettingsProfileSchema = providerSettingsProfileTabSchema;
export type ProviderSettingsProfileFormValues = ProviderSettingsProfileTabFormValues;

export const providerSettingsProfileFieldLabels: Record<string, string> = {
  phone: 'Phone number',
  street: 'Street address',
  city: 'City',
  state: 'State',
  zipCode: 'ZIP code',
  languages: 'Languages',
  bio: 'Profile bio',
  specialties: 'Clinical specialties',
  modalities: 'Treatment modalities',
  ageGroups: 'Preferred patient age groups',
};

export const providerSettingsHeadshotSchema = z
  .object({
    avatarFile: optionalFileSchema,
    hasExistingHeadshot: z.boolean(),
  })
  .superRefine((data, ctx) => {
    if (!data.avatarFile && !data.hasExistingHeadshot) {
      ctx.addIssue({
        code: 'custom',
        message: 'Required',
        path: ['avatarFile'],
        input: data.avatarFile,
      });
    }
  });

export type ProviderSettingsHeadshotFormValues = z.infer<typeof providerSettingsHeadshotSchema>;

export const providerSettingsHeadshotFieldLabels: Record<string, string> = {
  avatarFile: 'Professional headshot',
};

export const providerSettingsClinicalFieldLabels: Record<string, string> = {
  caqhId: 'CAQH ID',
  caqhUsername: 'CAQH username',
  caqhPassword: 'CAQH password',
};

export const licenseModalSchema = z.object({
  licenseName: nonEmptyString('Complete this field.'),
  licenseNumber: z.string(),
  jurisdictionState: z.string().nullable(),
  licenseStatus: z.string(),
  issueDate: isoDateString.nullable(),
  expirationDate: isoDateString.nullable(),
  licenseType: z.string().nullable(),
  isPrimary: z.boolean(),
  superseded: z.boolean(),
  documentFile: optionalFileSchema,
});

export type LicenseModalFormValues = z.infer<typeof licenseModalSchema>;

export const boardCertModalSchema = z.object({
  boardCertificationName: nonEmptyString('Complete this field.'),
  certType: z.string().nullable(),
  certificationNumber: z.string(),
  certificationDate: isoDateString.nullable(),
  certificateFile: optionalFileSchema,
});

export type BoardCertModalFormValues = z.infer<typeof boardCertModalSchema>;

export const deaModalSchema = z.object({
  deaNumberName: nonEmptyString('Complete this field.'),
  active: z.boolean(),
  expirationDate: isoDateString.nullable(),
  drugSchedules: z.string(),
  businessActivity: z.string(),
});

export type DeaModalFormValues = z.infer<typeof deaModalSchema>;

export const supplementalModalSchema = z.object({
  trainingName: nonEmptyString('Complete this field.'),
  completedDate: isoDateString.nullable(),
  trainingType: z.string().nullable(),
});

export type SupplementalModalFormValues = z.infer<typeof supplementalModalSchema>;

export const trainingModalSchema = z
  .object({
    trainingType: nonEmptyString('Complete this field.'),
    completedDate: isoDateString.nullable(),
  })
  .refine((data) => data.completedDate !== null, {
    message: 'Complete this field.',
    path: ['completedDate'],
  });

export type TrainingModalFormValues = z.infer<typeof trainingModalSchema>;

export const caqhSchema = z.object({
  username: nonEmptyString('Required'),
  password: nonEmptyString('Required'),
  id: nonEmptyString('Required'),
  attestationDate: isoDateString.nullable(),
});

export type CaqhFormValues = z.infer<typeof caqhSchema>;

export const educationEntrySchema = z.object({
  institution: optionalTrimmedString,
  degree: optionalTrimmedString,
  graduationYear: optionalTrimmedString,
});

export type EducationEntryFormValues = z.infer<typeof educationEntrySchema>;

export const educationModalSchema = z
  .object({
    institution: z.string(),
    degree: z.string(),
    graduationYear: z.string(),
  })
  .superRefine((data, ctx) => {
    if (data.graduationYear && !/^\d{4}$/.test(data.graduationYear)) {
      ctx.addIssue({
        code: 'custom',
        message: 'Enter a 4-digit year',
        path: ['graduationYear'],
        input: data.graduationYear,
      });
    }
  });

export type EducationModalFormValues = z.infer<typeof educationModalSchema>;

const licenseEntrySchema = z.object({
  licenseName: nonEmptyString('Complete this field.'),
  licenseNumber: z.string(),
  jurisdictionState: z.string().nullable(),
  licenseStatus: z.string(),
  issueDate: isoDateString.nullable(),
  expirationDate: isoDateString.nullable(),
  licenseType: z.string().nullable(),
  primaryLicense: z.boolean(),
});

export const licenseEducationEntrySchema = z.object({
  id: z.string(),
  licenseName: z.string(),
  licenseNumber: z.string(),
  jurisdictionState: z.string().nullable(),
  licenseStatus: z.string(),
  issueDate: isoDateString.nullable(),
  expirationDate: isoDateString.nullable(),
  licenseType: z.string().nullable(),
  isPrimary: z.boolean(),
  superseded: z.boolean(),
  documentFileName: z.string().optional(),
});

const certEntrySchema = z.object({
  boardCertificationName: nonEmptyString('Complete this field.'),
  certType: z.string().nullable(),
  certificationNumber: z.string(),
  certificationDate: isoDateString.nullable(),
});

export const certEducationEntrySchema = z.object({
  id: z.string(),
  boardCertificationName: z.string(),
  certType: z.string().nullable(),
  certificationNumber: z.string(),
  certificationDate: isoDateString.nullable(),
  certificateFileName: z.string().optional(),
});

const deaEntrySchema = z.object({
  deaNumberName: nonEmptyString('Complete this field.'),
  active: z.boolean(),
  expirationDate: isoDateString.nullable(),
  drugSchedules: z.string(),
  businessActivity: z.string(),
});

export const deaEducationEntrySchema = z.object({
  id: z.string(),
  deaNumberName: z.string(),
  active: z.boolean(),
  expirationDate: isoDateString.nullable(),
  drugSchedules: z.string(),
  businessActivity: z.string(),
});

const trainingEntrySchema = z.object({
  trainingName: nonEmptyString('Complete this field.'),
  completedDate: isoDateString.nullable(),
  trainingType: z.string().nullable(),
});

export const specialtyTrainingEducationEntrySchema = z.object({
  id: z.string(),
  trainingName: z.string(),
  completedDate: isoDateString.nullable(),
  trainingType: z.string().nullable(),
});

export const completedTrainingEntrySchema = z.object({
  id: z.string(),
  trainingType: z.string(),
  completedDate: z.string(),
});

const educationListEntrySchema = z.object({
  id: z.string(),
  institution: z.string(),
  degree: z.string(),
  graduationYear: z.string(),
});

export const licensesEducationSchema = z.object({
  caqhUsername: nonEmptyString('Required'),
  caqhPassword: nonEmptyString('Required'),
  caqhId: nonEmptyString('Required'),
  caqhAttestationDate: isoDateString.nullable(),
  educationEntries: z.array(educationListEntrySchema),
  licenses: z.array(licenseEducationEntrySchema),
  certifications: z.array(certEducationEntrySchema),
  deaCredentials: z.array(deaEducationEntrySchema),
  specialtyTraining: z.array(specialtyTrainingEducationEntrySchema),
  trainings: z.array(completedTrainingEntrySchema),
});

export type LicensesEducationFormValues = z.infer<typeof licensesEducationSchema>;

export const licensesEducationFieldLabels: Record<string, string> = {
  caqhUsername: 'CAQH username',
  caqhPassword: 'CAQH password',
  caqhId: 'CAQH ID',
};

export const licensesFormSchema = z.object({
  degree: z.string(),
  graduationYear: z.string(),
  licenses: z.array(licenseEntrySchema),
  certifications: z.array(certEntrySchema),
  deaCredentials: z.array(deaEntrySchema),
  specialtyTraining: z.array(trainingEntrySchema),
});

export type LicensesFormValues = z.infer<typeof licensesFormSchema>;
